// SolyticsFooter.jsx
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import SolyticsBox from './SolyticsBox';

// ─── Style helpers ───────────────────────────────────────────────────────────

const getFooterWrapperStyles = (theme, showBorder) => ({
  backgroundColor: theme.palette.background.paper,
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 'auto',
  position: 'sticky',
  bottom: 0,
  zIndex: theme.zIndex.appBar + 5,
  ...(showBorder && {
    borderTop: `1px solid ${theme.palette.divider}`,
  }),
});

const getFooterContentStyles = (justify) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: justify,
  gap: '16px',
  px: '24px',
  py: '16px',
  width: '100%',
  boxSizing: 'border-box',
});

/**
 * SolyticsFooter
 *
 * A reusable container for page-level action buttons (e.g., Save, Cancel, Next).
 * Strictly follows the Figma design specifications:
 * - 24px horizontal padding
 * - 16px vertical padding
 * - 16px gap between children
 * - Content alignment configurable (default: right / flex-end)
 * - Solid 1px top border spanning full width (edge-to-edge, no padding inset)
 * - Automatically supports Dark/Light mode depending on the active Theme.
 *
 * Usage:
 * <SolyticsFooter>
 *   <SolyticsButton variant="outlined">Reset</SolyticsButton>
 *   <SolyticsButton variant="contained">Save</SolyticsButton>
 * </SolyticsFooter>
 */
const SolyticsFooter = ({
  children,
  justify = 'flex-end',
  showBorder = true,
}) => {
  return (
    <SolyticsBox
      component="footer"
      role="region"
      aria-label="Page actions"
      className="solytics-footer-container"
      sx={(theme) => getFooterWrapperStyles(theme, showBorder)}
    >
      <SolyticsBox sx={getFooterContentStyles(justify)}>{children}</SolyticsBox>
    </SolyticsBox>
  );
};

SolyticsFooter.propTypes = {
  /**
   * Action buttons to render inside the footer.
   */
  children: PropTypes.node.isRequired,
  /**
   * Horizontal alignment of the footer content.
   * Maps to CSS `justify-content`.
   * @default 'flex-end'
   */
  justify: PropTypes.oneOf([
    'flex-start',
    'center',
    'flex-end',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
  /**
   * Whether to show the top border separator.
   * @default true
   */
  showBorder: PropTypes.bool,
};

export default memo(SolyticsFooter);
